import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Badge } from "@/components/ui/badge";
import { KanbanCard } from "./KanbanCard";
import type { ComplianceTask } from "@/hooks/use-compliance-tasks";

interface ColumnDef {
  id: ComplianceTask["status"];
  label: string;
  color: string;
  badgeColor: string;
}

interface KanbanColumnProps {
  column: ColumnDef;
  tasks: ComplianceTask[];
  onTaskClick: (task: ComplianceTask) => void;
  getDepartmentColor: (id: string | null) => string;
  getDepartmentName: (id: string | null) => string;
  isOverdue: (task: ComplianceTask) => boolean;
  getDaysUntilDue: (due: string | null) => number | null;
}

export function KanbanColumn({ column, tasks, onTaskClick, getDepartmentColor, getDepartmentName, isOverdue, getDaysUntilDue }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  return (
    <div className="flex flex-col w-72 flex-shrink-0">
      <div className={`rounded-t-lg px-3 py-2.5 ${column.color}`}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold">{column.label}</span>
          <Badge variant="secondary" className={`text-xs h-5 ${column.badgeColor}`}>
            {tasks.length}
          </Badge>
        </div>
      </div>

      <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
        <div
          ref={setNodeRef}
          className={`flex-1 min-h-48 rounded-b-lg p-2 space-y-2 transition-colors ${
            isOver ? "bg-accent/60" : "bg-muted/30"
          }`}
        >
          {tasks.map((task) => (
            <KanbanCard
              key={task.id}
              task={task}
              onClick={() => onTaskClick(task)}
              departmentColor={getDepartmentColor(task.department_id)}
              departmentName={getDepartmentName(task.department_id)}
              isOverdue={isOverdue(task)}
              daysUntilDue={getDaysUntilDue(task.due_date)}
            />
          ))}
        </div>
      </SortableContext>
    </div>
  );
}
